/**
 * THE BOOT HYDRATION — the one place the app's read endpoints are called from.
 *
 * ============================================================================
 * WHAT THIS SOLVES. Batch 7 §1 asks, for every feature, "where the call lives". Until now
 * the answer for every READ was "nowhere": the six `getX()` functions in src/api/ had no
 * callers, because each store seeded itself from the fixture directly. So the fixture was
 * the store's initial state rather than the mock's response, and a backend arriving would
 * have meant editing six stores rather than six API bodies.
 *
 * Each store now has a `loadX()` action that fetches through its seam. This is where those
 * are called — once, high in the tree, on mount. One hook rather than a `useEffect` in six
 * screens, for the reason hooks/index.ts gives about the other app-wide effects: a call per
 * route would re-fetch the library on every navigation, and six screens each deciding when
 * to hydrate is six chances to disagree.
 * ============================================================================
 *
 * ============================================================================
 * AND IT DOES NOT RUN WHILE THE APP IS ON FIXTURES. `USE_MOCK_API` gates the whole effect,
 * and that is the load-bearing decision in this file rather than a shortcut.
 *
 * In mock mode every one of these reads returns the SAME fixture the store is already
 * seeded with — or, for playlists and projects, the empty set. So firing them on boot would
 * buy nothing and cost two real things:
 *
 *   IT WOULD DISCARD THE SESSION. `loadSongs` replaces the library with what `getSongs()`
 *   returns, which is a fresh `createInitialSongs()` — so a song generated a moment ago, and
 *   every favourite, rename and version added since load, would vanish behind a spinner. A
 *   second call mid-session would do it again.
 *
 *   IT WOULD PUT A SPINNER OVER DATA THAT IS ALREADY THERE. 350ms of MOCK_LATENCY_MS on
 *   every boot, to arrive at the state the app started in.
 *
 * WHEN `VITE_API_BASE_URL` IS SET, the effect runs — and every module in src/api/ is still
 * a mock, so `assertMock` throws inside each one. That is not a bug being introduced; it is
 * mock.ts's designed behaviour ("Failing loudly is the point"), and it lands in each store's
 * `load.error` as a readable message naming the function to implement. Pointing the frontend
 * at a backend therefore produces a precise list of unimplemented reads rather than an app
 * that silently serves fixtures against a real host.
 * ============================================================================
 *
 * WHAT IT DELIBERATELY DOES NOT LOAD:
 *
 *   THE SESSION. `authStore.restoreSession()` is not called here. Session restoration has to
 *   happen before the router decides whether to show a route or a login screen, and it has to
 *   happen in mock mode too — it is the one read whose mock answer ("nobody") is truthful
 *   rather than a fixture. It is also the read whose result decides whether the others are
 *   worth making. Wiring it belongs with the route gate that consumes `sessionChecked`,
 *   which nothing does yet; see authStore.
 *
 *   THE CREDIT LEDGER. `loadTransactions()` replaces the session's in-memory transactions
 *   with the server's history, and the mock's history is `[]` — so in mock mode it would
 *   erase the ledger the session built. It is called by the first screen that displays a
 *   history, which is none of them today. See creditsStore.
 *
 *   THE SUPPORT TICKETS. `loadTickets()` has the same shape as `loadSongs` and the same
 *   objection, and the Support Center is one screen rather than app-wide state — so it
 *   hydrates from its own route when there is a backend to hydrate from, not from the shell.
 *
 * Called exactly once, from AppShell, alongside `useSimulatedPlayback()` and
 * `useSyncUnlimited()` — see hooks/index.ts on why those two have one call site.
 */
import { useEffect, useState } from 'react';

import { USE_MOCK_API } from '../config';
import { useBonusClaimsStore } from '../stores/bonusClaimsStore';
import { useCreditsStore } from '../stores/creditsStore';
import { useEntitlementsStore } from '../stores/entitlementsStore';
import { usePlaylistsStore } from '../stores/playlistsStore';
import { usePublisherStore } from '../stores/publisherStore';
import { useSongsStore } from '../stores/songsStore';
import { useStudioProjectsStore } from '../stores/studioProjectsStore';

/**
 * Fetches the account's general data once, and Publisher only once access is confirmed,
 * when a backend is configured.
 *
 * Returns nothing. Each store holds its own `load` state (see stores/loadState.ts), which
 * is what a screen reads to show a skeleton, an error or a confirmed empty state — a
 * combined status returned from here would be one status for seven independent requests,
 * and the Library has no reason to wait on the entitlement read.
 */
export function useAppData(): void {
  const publisher = useEntitlementsStore((s) => s.publisher);
  const entitlementsLoad = useEntitlementsStore((s) => s.load.status);
  const publisherLoad = usePublisherStore((s) => s.load.status);
  const [requestedEntitlements, setRequestedEntitlements] = useState(false);

  useEffect(() => {
    // The fixtures are already in the stores. See the header for the two costs of
    // re-fetching them and for what happens the moment a base URL is set.
    if (USE_MOCK_API) return;

    /*
      Read through `getState()` rather than seven selector subscriptions. The actions are
      stable closures, so subscribing would add nothing except a re-render of the app shell
      for any change in any of seven stores — while this hook renders nothing at all.

      Fired together and not awaited in sequence: these are seven independent endpoints and
      nothing here depends on another's result, so serialising them would make the slowest
      one everybody's wait. Each store records its own outcome, and none of the `loadX`
      actions reject.
    */
    void useSongsStore.getState().loadSongs();
    void usePlaylistsStore.getState().loadPlaylists();
    void usePlaylistsStore.getState().loadPlaylistSongs();
    void useStudioProjectsStore.getState().loadProjects();
    void useCreditsStore.getState().loadBalance();
    void useEntitlementsStore.getState().loadEntitlements();
    void useBonusClaimsStore.getState().loadClaims();
    // Set after asking, so a stale entitlement snapshot from a previous session cannot
    // trigger Publisher before this mount's own entitlement read has had a chance to run.
    setRequestedEntitlements(true);
  }, []);

  useEffect(() => {
    if (USE_MOCK_API) return;
    if (!requestedEntitlements) return;
    if (entitlementsLoad !== 'ready' || !publisher) return;
    if (publisherLoad !== 'idle') return;

    /*
      Separate from the generic boot bundle on purpose. Publisher is an add-on entitlement,
      not a base-session fact, so asking for it before the entitlement read answers turns a
      normal non-owner into a predictable 403. Once the account is known to own Publisher,
      the same store action hydrates it.
    */
    void usePublisherStore.getState().loadPublisher();
  }, [entitlementsLoad, publisher, publisherLoad, requestedEntitlements]);
}
